import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Upload, FileText, CheckCircle, AlertCircle, X } from 'lucide-react';

const DragDropUpload = ({ 
  onFileUpload, 
  acceptedTypes = ".pdf,.doc,.docx,.txt",
  maxSize = 10 * 1024 * 1024, // 10MB
  className = "",
  disabled = false
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('idle'); // idle, uploading, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const fileInputRef = useRef(null);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter(prev => prev + 1);
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter(prev => prev - 1);
    if (dragCounter <= 1) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    setDragCounter(0);

    if (disabled) return;

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelection(files[0]);
    }
  };

  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      handleFileSelection(files[0]);
    }
  };

  const handleFileSelection = async (file) => {
    // Reset previous states
    setErrorMessage('');
    setUploadStatus('uploading');

    // Validate file type
    const allowedExtensions = acceptedTypes.split(',').map(ext => ext.trim());
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!allowedExtensions.includes(fileExtension)) {
      setErrorMessage(`File type not supported. Please upload: ${acceptedTypes}`);
      setUploadStatus('error');
      return;
    }

    // Validate file size
    if (file.size > maxSize) {
      setErrorMessage(`File too large. Maximum size is ${(maxSize / (1024 * 1024)).toFixed(1)}MB`);
      setUploadStatus('error');
      return;
    }

    try {
      setUploadedFile(file);
      await onFileUpload(file);
      setUploadStatus('success');
    } catch (error) {
      setErrorMessage(error.message || 'Upload failed. Please try again.');
      setUploadStatus('error');
      setUploadedFile(null);
    }
  };

  const resetUpload = () => {
    setUploadStatus('idle');
    setErrorMessage('');
    setUploadedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getStatusStyles = () => {
    if (disabled) return 'border-slate-200 bg-slate-50 cursor-not-allowed';
    if (uploadStatus === 'success') return 'border-green-300 bg-green-50';
    if (uploadStatus === 'error') return 'border-red-300 bg-red-50';
    if (isDragging) return 'border-blue-500 bg-blue-50 border-solid';
    return 'border-slate-300 hover:border-blue-400';
  };

  const getIconColor = () => {
    if (disabled) return 'text-slate-400';
    if (uploadStatus === 'success') return 'text-green-500';
    if (uploadStatus === 'error') return 'text-red-500';
    if (isDragging) return 'text-blue-500';
    return 'text-slate-400';
  };

  return (
    <div className={`relative ${className}`}>
      <div
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-2xl p-8 text-center transition-all duration-200 ${getStatusStyles()}`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedTypes}
          onChange={handleFileInputChange}
          className="hidden"
          id="file-upload"
          disabled={disabled}
        />

        {uploadStatus === 'uploading' ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="py-4"
          >
            <div className="animate-spin w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-blue-600 font-medium">Processing your file...</p>
          </motion.div>
        ) : uploadStatus === 'success' ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="py-4"
          >
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <p className="text-green-700 font-medium mb-2">
              📄 {uploadedFile?.name} uploaded successfully!
            </p>
            <p className="text-green-600 text-sm mb-4">
              File processed and ready for analysis
            </p>
            <button
              onClick={resetUpload}
              className="inline-flex items-center px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload Different File
            </button>
          </motion.div>
        ) : uploadStatus === 'error' ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="py-4"
          >
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <p className="text-red-700 font-medium mb-2">Upload Failed</p>
            <p className="text-red-600 text-sm mb-4">{errorMessage}</p>
            <button
              onClick={resetUpload}
              className="inline-flex items-center px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
            >
              <X className="w-4 h-4 mr-2" />
              Try Again
            </button>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="py-4"
          >
            <motion.div
              animate={isDragging ? { scale: 1.1 } : { scale: 1 }}
              transition={{ duration: 0.2 }}
            >
              {isDragging ? (
                <FileText className={`w-16 h-16 mx-auto mb-4 ${getIconColor()}`} />
              ) : (
                <Upload className={`w-16 h-16 mx-auto mb-4 ${getIconColor()}`} />
              )}
            </motion.div>
            
            <p className={`text-lg font-medium mb-2 ${
              disabled ? 'text-slate-500' : isDragging ? 'text-blue-600' : 'text-slate-700'
            }`}>
              {isDragging 
                ? 'Drop your file here' 
                : disabled 
                  ? 'File upload disabled'
                  : 'Drag & drop your resume here'
              }
            </p>
            
            {!disabled && (
              <>
                <p className="text-slate-500 mb-4">or</p>
                <label
                  htmlFor="file-upload"
                  className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 cursor-pointer transition-colors font-medium"
                >
                  <Upload className="w-5 h-5 mr-2" />
                  Choose File
                </label>
                <p className="text-sm text-slate-500 mt-4">
                  Supports: {acceptedTypes.replace(/\./g, '').toUpperCase()} • 
                  Max size: {(maxSize / (1024 * 1024)).toFixed(1)}MB
                </p>
              </>
            )}
          </motion.div>
        )}
      </div>

      {/* Drag overlay */}
      {isDragging && !disabled && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-blue-500/20 border-2 border-blue-500 border-solid rounded-2xl flex items-center justify-center z-10"
        >
          <div className="text-center">
            <FileText className="w-16 h-16 text-blue-600 mx-auto mb-2" />
            <p className="text-blue-700 font-semibold text-lg">
              Release to upload
            </p>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default DragDropUpload;